// src/pages/AIForSchoolsPage.js
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
} from "@mui/material";
import AOS from "aos";
import "aos/dist/aos.css";

export default function AIForSchoolsPage() {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top when this page loads
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    AOS.init({ duration: 800, once: true });
  }, []);

  const textOnDark = {
    color: "#F9FAFB",
    fontFamily: "Montserrat, sans-serif",
  };

  const sectionTitle = {
    ...textOnDark,
    fontWeight: 700,
    marginBottom: 8,
  };

  const sectionSubtitle = {
    ...textOnDark,
    opacity: 0.9,
    marginBottom: 24,
  };

  const cardStyle = {
    background: "rgba(255,255,255,0.96)",
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(15,23,42,0.35)",
    height: "100%",
  };

  const mutedLabel = {
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#CBD5F5",
    marginBottom: 4,
  };

  const footerLinkStyle = {
    color: "white",
    textDecoration: "none",
  };

  return (
    <div
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        color: "#F9FAFB",
        fontFamily: "Montserrat, sans-serif",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* 🔹 Standard header (consistent with other pages) */}
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav
          style={{
            display: "flex",
            gap: "15px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={() => navigate("/help")}
            className="header-button"
          >
            Help
          </button>
          <button
            onClick={() => navigate("/subscriptions")}
            className="header-button"
          >
            View Plans
          </button>
          <button
            onClick={() => navigate("/")}
            className="header-button"
          >
            Home
          </button>
        </nav>
      </header>

      {/* MAIN CONTENT with dark overlay */}
      <main
        style={{
          background:
            "linear-gradient(to bottom, rgba(15,23,42,0.88), rgba(15,23,42,0.96))",
          flexGrow: 1,
        }}
      >
        {/* Hero */}
        <Container maxWidth="lg" style={{ paddingTop: 40, paddingBottom: 30 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7} data-aos="fade-right">

              <Typography style={mutedLabel}>
                AI LITERACY · CRITICAL THINKING · READING
              </Typography>
              <Typography
                variant="h3"
                style={{
                  ...textOnDark,
                  fontWeight: 800,
                  lineHeight: 1.1,
                  marginBottom: 12,
                }}
              >
                AI in YourStoryWorld:
                <br />
                Teacher Guide for the Classroom
              </Typography>
              <Typography variant="body1" style={sectionSubtitle}>
                Use YourStoryWorld to support reading engagement <b>and</b> AI
                awareness. These ready-to-use mini-lessons help pupils
                understand how AI works, practise prompting, and separate
                fiction from fact – all through safe, engaging stories.
              </Typography>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <Chip
                  label="Suggested years: KS2–KS3"
                  style={{
                    backgroundColor: "rgba(55,65,81,0.85)",
                    color: "#F9FAFB",
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label="AI & Digital Literacy"
                  style={{
                    backgroundColor: "rgba(59,130,246,0.9)",
                    color: "#F9FAFB",
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label="Fact vs Fiction"
                  style={{
                    backgroundColor: "rgba(16,185,129,0.9)",
                    color: "#F9FAFB",
                    fontWeight: 600,
                  }}
                />
              </div>

              <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: "#3B82F6",
                    color: "white",
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                  onClick={() => navigate("/Schools")}
                >
                  Back to School Overview
                </Button>
                <Button
                  variant="outlined"
                  style={{
                    borderColor: "#E5E7EB",
                    color: "#E5E7EB",
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                  onClick={() => navigate("/ai-for-schools")}
                >
                  AI for Schools Overview
                </Button>
              </div>
            </Grid>

            <Grid item xs={12} md={5} data-aos="fade-left">
              <Card style={cardStyle}>
                <CardContent>
                  <Typography
                    variant="h6"
                    style={{ fontWeight: 700, marginBottom: 8 }}
                  >
                    At a glance
                  </Typography>
                  <Typography variant="body2" style={{ marginBottom: 10 }}>
                    This guide is designed so teachers can:
                  </Typography>
                  <ul
                    style={{
                      paddingLeft: "1.1rem",
                      marginTop: 0,
                      marginBottom: 8,
                      color: "#111827",
                      fontSize: "0.9rem",
                    }}
                  >
                    <li>Introduce what AI is (in pupil-friendly language).</li>
                    <li>
                      Show how prompts change the stories AI generates.
                    </li>
                    <li>
                      Practise spotting what is made up vs what sounds factual.
                    </li>
                    <li>
                      Discuss why we still need to check information from AI.
                    </li>
                  </ul>
                  <Typography
                    variant="body2"
                    style={{ fontSize: "0.8rem", color: "#6B7280" }}
                  >
                    Use as a short sequence, or dip into individual activities
                    during computing, English, PSHE or digital literacy lessons.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Why AI + reading section */}
        <Container maxWidth="lg" style={{ paddingBottom: 20 }}>
          <Typography
            variant="h5"
            align="center"
            style={sectionTitle}
            data-aos="fade-up"
          >
            How YourStoryWorld Supports AI & Digital Literacy
          </Typography>
          <Typography
            variant="body1"
            align="center"
            style={sectionSubtitle}
            data-aos="fade-up"
          >
            YourStoryWorld isn’t just a reading tool – it’s a safe way for
            pupils to see AI in action, reflect on how it works, and think
            critically about the stories it produces.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4} data-aos="zoom-in">
              <Card style={cardStyle}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    style={{ ...mutedLabel, color: "#6B7280" }}
                  >
                    PROMPTS & OUTPUTS
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{ fontWeight: 700, marginBottom: 6 }}
                  >
                    Understanding “What we tell AI”
                  </Typography>
                  <Typography variant="body2">
                    Pupils learn that AI doesn’t “think” like a person – it
                    responds to what we ask it. By tweaking prompts in
                    YourStoryWorld, they can see how small changes lead to
                    different stories, characters and tones.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4} data-aos="zoom-in">
              <Card style={cardStyle}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    style={{ ...mutedLabel, color: "#6B7280" }}
                  >
                    FACT VS FICTION
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{ fontWeight: 700, marginBottom: 6 }}
                  >
                    Spotting what’s made up
                  </Typography>
                  <Typography variant="body2">
                    By comparing AI-generated stories with real-world knowledge,
                    pupils practise asking: “Is this believable? How do we
                    know?” This supports critical reading and media literacy in
                    an age-appropriate way.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4} data-aos="zoom-in">
              <Card style={cardStyle}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    style={{ ...mutedLabel, color: "#6B7280" }}
                  >
                    SAFE & GUIDED
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{ fontWeight: 700, marginBottom: 6 }}
                  >
                    Child-safe AI by design
                  </Typography>
                  <Typography variant="body2">
                    Stories are automatically screened to block violent,
                    explicit, discriminatory or otherwise inappropriate content.
                    Teachers stay in control, and pupils can explore stories in
                    a safeguarded environment.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Lesson sequence overview */}
        <Container maxWidth="lg" style={{ paddingTop: 30, paddingBottom: 20 }}>
          <Typography
            variant="h5"
            align="center"
            style={sectionTitle}
            data-aos="fade-up"
          >
            5-Session AI & Storytelling Mini-Unit
          </Typography>
          <Typography
            variant="body1"
            align="center"
            style={sectionSubtitle}
            data-aos="fade-up"
          >
            Each session can run in 20–40 minutes. Adapt the language and
            depth to suit your year group.
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                title: "Lesson 1 · What is AI?",
                desc: "Introduce AI using YourStoryWorld as an example: a tool that creates stories from instructions.",
              },
              {
                title: "Lesson 2 · Prompts & Changes",
                desc: "Experiment with changing prompts and see how the story output changes.",
              },
              {
                title: "Lesson 3 · Fact or Fiction?",
                desc: "Read an AI-generated story and identify which parts are realistic, invented or exaggerated.",
              },
              {
                title: "Lesson 4 · Improve the Prompt",
                desc: "Work as a class to make prompts clearer, kinder, safer and more specific.",
              },
              {
                title: "Lesson 5 · Checking AI",
                desc: "Discuss when to trust AI, when to double-check, and how to ask for safer, more accurate output.",
              },
            ].map((l, idx) => (
              <Grid item xs={12} md={4} key={idx} data-aos="fade-up">
                <Card style={cardStyle}>
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      style={{ ...mutedLabel, color: "#6B7280" }}
                    >
                      SESSION {idx + 1}
                    </Typography>
                    <Typography
                      variant="h6"
                      style={{ fontWeight: 700, marginBottom: 6 }}
                    >
                      {l.title}
                    </Typography>
                    <Typography variant="body2">{l.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* Full lesson detail */}
        <Container maxWidth="lg" style={{ paddingTop: 20, paddingBottom: 40 }}>
          <Typography
            variant="h5"
            style={sectionTitle}
            data-aos="fade-up"
          >
            Lesson-by-Lesson Guide
          </Typography>
          <Typography
            variant="body1"
            style={sectionSubtitle}
            data-aos="fade-up"
          >
            Use these as written or trim them down as starters / plenaries.
          </Typography>

          {/* Lesson 1 */}
          <Card style={{ ...cardStyle, marginBottom: 20 }} data-aos="fade-up">
            <CardContent>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                Lesson 1 – What is AI and how does it write stories?
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>
                <b>Suggested years:</b> KS2–KS3 · <b>Time:</b> 20–30 mins
              </Typography>
              <Typography
                variant="body2"
                style={{ marginTop: 10, fontWeight: 600 }}
              >
                Aim
              </Typography>
              <Typography variant="body2">
                Pupils understand that AI is a computer system that predicts
                words based on patterns, not a person that “knows everything”.
              </Typography>

              <Typography
                variant="body2"
                style={{ marginTop: 10, fontWeight: 600 }}
              >
                Teacher setup
              </Typography>
              <ul style={{ paddingLeft: "1.1rem", marginTop: 4 }}>
                <li>
                  Open YourStoryWorld on the board and generate a simple story
                  together (e.g. “A kind dragon who helps at school”).
                </li>
                <li>
                  Ask pupils what they notice about the story – tone, structure,
                  vocabulary.
                </li>
              </ul>

              <Typography
                variant="body2"
                style={{ marginTop: 10, fontWeight: 600 }}
              >
                In-class activity
              </Typography>
              <ul style={{ paddingLeft: "1.1rem" }}>
                <li>
                  Ask: “Do you think a person typed all of this, one word at a
                  time?” – then explain that an AI system predicted likely
                  words.
                </li>
                <li>
                  Explain in simple language: AI looks at many examples and
                  guesses what comes next. It doesn’t “understand” like a
                  human.
                </li>
                <li>
                  Introduce the word <b>prompt</b>: the instructions we give to
                  AI before it generates the story.
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Lesson 2 */}
          <Card style={{ ...cardStyle, marginBottom: 20 }} data-aos="fade-up">
            <CardContent>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                Lesson 2 – Prompts: how our instructions change the story
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>
                <b>Suggested years:</b> KS2–KS3 · <b>Time:</b> 25–35 mins
              </Typography>

              <Typography
                variant="body2"
                style={{ marginTop: 10, fontWeight: 600 }}
              >
                Aim
              </Typography>
              <Typography variant="body2">
                Pupils see how changing the prompt (characters, setting, tone)
                changes the AI-generated story.
              </Typography>

              <Typography
                variant="body2"
                style={{ marginTop: 10, fontWeight: 600 }}
              >
                Activity
              </Typography>
              <ul style={{ paddingLeft: "1.1rem" }}>
                <li>
                  Start with a base prompt in YourStoryWorld (e.g. “Two
                  friends explore a mysterious forest.”).
                </li>
                <li>
                  Ask pupils to suggest small changes: new setting, different
                  main character, new goal.
                </li>
                <li>
                  Generate 2–3 variants and quickly compare: What stayed the
                  same? What changed?
                </li>
                <li>
                  Discuss: “What did we change in the prompt that made the
                  story different?”
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Lesson 3 */}
          <Card style={{ ...cardStyle, marginBottom: 20 }} data-aos="fade-up">
            <CardContent>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                Lesson 3 – Fact vs Fiction in AI-generated stories
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>
                <b>Suggested years:</b> KS2–KS3 · <b>Time:</b> 30–40 mins
              </Typography>

              <Typography
                variant="body2"
                style={{ marginTop: 10, fontWeight: 600 }}
              >
                Aim
              </Typography>
              <Typography variant="body2">
                Pupils practise identifying which aspects of a story are clearly
                fictional and which sound like they could be real.
              </Typography>

              <Typography
                variant="body2"
                style={{ marginTop: 10, fontWeight: 600 }}
              >
                Activity
              </Typography>
              <ul style={{ paddingLeft: "1.1rem" }}>
                <li>
                  Generate a story with some realistic elements (a real city, a
                  school setting) and some obviously fictional ones (talking
                  animals, magical objects).
                </li>
                <li>
                  In pairs, pupils highlight or list:
                  <ul style={{ paddingLeft: "1.1rem", marginTop: 4 }}>
                    <li>Things that are definitely made up.</li>
                    <li>Things that could be real, but we’d need to check.</li>
                  </ul>
                </li>
                <li>
                  Plenary: “Can AI invent things? Should we always believe
                  everything it writes?”
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Lesson 4 */}
          <Card style={{ ...cardStyle, marginBottom: 20 }} data-aos="fade-up">
            <CardContent>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                Lesson 4 – Improving prompts (clearer, kinder, safer)
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>
                <b>Suggested years:</b> Upper KS2–KS3 · <b>Time:</b> 25–35 mins
              </Typography>

              <Typography
                variant="body2"
                style={{ marginTop: 10, fontWeight: 600 }}
              >
                Aim
              </Typography>
              <Typography variant="body2">
                Pupils learn how to write better prompts that are specific,
                respectful and age-appropriate.
              </Typography>

              <Typography
                variant="body2"
                style={{ marginTop: 10, fontWeight: 600 }}
              >
                Activity
              </Typography>
              <ul style={{ paddingLeft: "1.1rem" }}>
                <li>
                  Show a vague prompt (e.g. “Write a story about a boy.”) and a
                  more detailed one (e.g. “Write a short, positive story about a
                  boy who helps his friend at school when they feel nervous.”).
                </li>
                <li>
                  Compare the outputs. Which story was more helpful? Why?
                </li>
                <li>
                  Ask pupils to rewrite weak prompts into stronger ones, making
                  sure they are:
                  <ul style={{ paddingLeft: "1.1rem", marginTop: 4 }}>
                    <li>Specific about characters and goals.</li>
                    <li>Kind and respectful to all groups.</li>
                    <li>Safe and age-appropriate.</li>
                  </ul>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Lesson 5 */}
          <Card style={{ ...cardStyle }} data-aos="fade-up">
            <CardContent>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                Lesson 5 – Can we trust AI? When to double-check
              </Typography>
              <Typography variant="body2" style={{ marginTop: 4 }}>
                <b>Suggested years:</b> Upper KS2–KS3 · <b>Time:</b> 20–30 mins
              </Typography>

              <Typography
                variant="body2"
                style={{ marginTop: 10, fontWeight: 600 }}
              >
                Aim
              </Typography>
              <Typography variant="body2">
                Pupils understand that AI can make mistakes, mix up facts, or
                sound confident even when wrong – so we still need to think and
                check.
              </Typography>

              <Typography
                variant="body2"
                style={{ marginTop: 10, fontWeight: 600 }}
              >
                Discussion prompts
              </Typography>
              <ul style={{ paddingLeft: "1.1rem" }}>
                <li>
                  “Should we use AI to help us with ideas?” (Yes, with
                  supervision and judgement.)
                </li>
                <li>
                  “Should we copy everything it says into our work?” (No – we
                  should read, think and adapt.)
                </li>
                <li>
                  “When is it important to check information with another
                  source?” (Homework, facts, news, real-world questions.)
                </li>
              </ul>
            </CardContent>
          </Card>
        </Container>
      </main>

      {/* 🔹 Standard footer (matches Landing & Subscriptions) */}
      <footer
        style={{
          textAlign: "center",
          padding: "20px",
          backgroundColor: "#000",
          color: "#fff",
        }}
      >
        {/* Company & rights */}
        <Typography variant="body2">
          © {new Date().getFullYear()} YourStoryWorld Ltd · Company No. 17098564 · All rights reserved.
        </Typography>

        {/* Main nav links */}
        <Typography variant="body2" style={{ marginTop: "8px" }}>
          <a href="/about" style={footerLinkStyle}>
            About Us
          </a>{" "}
          |{" "}
          <a href="/contact" style={footerLinkStyle}>
            Contact
          </a>{" "}
          |{" "}
          <a href="/help" style={footerLinkStyle}>
            Help & FAQs
          </a>
        </Typography>

        {/* Policy links */}
        <Typography variant="body2" style={{ marginTop: 4 }}>
          <a href="/privacy-policy" style={footerLinkStyle}>
            Privacy Policy
          </a>{" "}
          |{" "}
          <a href="/terms" style={footerLinkStyle}>
            Terms of Use
          </a>{" "}
          |{" "}
          <a href="/cookie-policy" style={footerLinkStyle}>
            Cookie Policy
          </a>{" "}
          |{" "}
          <a href="/child-safety" style={footerLinkStyle}>
            Child Safety
          </a>{" "}
          |{" "}
          <a href="/accessibility" style={footerLinkStyle}>
            Accessibility
          </a>{" "}
          |{" "}
          <a href="/data-protection" style={footerLinkStyle}>
            Data Protection &amp; Security
          </a>
        </Typography>

        {/* Disclaimer */}
        <Typography
          variant="caption"
          style={{ marginTop: "10px", display: "block", color: "#ccc" }}
        >
          ⚠️ Disclaimer: The stories generated by YourStoryWorld are created
          using AI and are intended for entertainment and educational purposes
          only. While we strive for accuracy, some content may contain factual
          or narrative inconsistencies.
          <p
            style={{
              fontSize: "0.8rem",
              color: "#e11d48",
              marginTop: "10px",
            }}
          >
            <strong>Child-Safe Content:</strong> YourStoryWorld uses AI safety
            filters to ensure all stories remain age-appropriate. Violent,
            explicit, discriminatory, or harmful content is automatically
            removed or rewritten. Minor variance may occur due to the nature of
            AI generation.
          </p>
        </Typography>
      </footer>
    </div>
  );
}
